import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { ScrollArea as FluidScrollArea } from '../fluid/scroll-area.js';
import { cx } from '../utils/cx.js';
import styles from './ScrollArea.module.css';

export type ScrollAreaOrientation = 'vertical' | 'horizontal' | 'both';

export type ScrollAreaProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  children: ReactNode;
  /**
   * Which axes get scrollbars. Default vertical.
   */
  orientation?: ScrollAreaOrientation;
  /**
   * Classes for the inner scrolling viewport (fade utilities live here).
   * Example: `scroll-fade` / `scroll-fade-x` (Fluid Functionalism) or legacy `wp-scroll-fade`.
   */
  viewportClassName?: string;
  /**
   * Edge fade on viewport. Default: y fade for vertical/both, x for horizontal.
   * Pass `false` / `'none'` to disable. Or pass explicit `'y'` / `'x'`.
   */
  fade?: boolean | 'y' | 'x' | 'none';
  /** @deprecated Prefer `className` + CSS height on the outer box. Kept for callers. */
  maxHeight?: string | number;
  /** @deprecated Prefer `className` + CSS width on the outer box. */
  maxWidth?: string | number;
  /** Extra props for the scrolling viewport (role, aria-*). */
  viewportProps?: HTMLAttributes<HTMLDivElement>;
};

function fadeClass(
  fade: ScrollAreaProps['fade'],
  orientation: ScrollAreaOrientation,
): string | undefined {
  if (fade === false || fade === 'none') return undefined;
  if (fade === 'x') return 'scroll-fade-x';
  if (fade === 'y') return 'scroll-fade';
  if (orientation === 'horizontal') return 'scroll-fade-x';
  return 'scroll-fade';
}

function toCssSize(v: string | number | undefined): string | undefined {
  if (v == null) return undefined;
  return typeof v === 'number' ? `${v}px` : v;
}

/**
 * Scroll host — Fluid Functionalism Base UI ScrollArea (PHI-327).
 *
 * - `className` — outer box (set height/width constraint here)
 * - `viewportClassName` — inner scroller (put `scroll-fade` / `scroll-fade-x` here)
 * - `ref` — scrolling viewport (DataTable virtualizer)
 * - touch-primary: native overflow (FF); fine pointer: Base UI thumbs
 * - mist rails skipped on `[data-slot="scroll-area*"]` (see mistRail.ts)
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollArea(
  {
    orientation = 'vertical',
    viewportClassName,
    fade,
    maxHeight = 'min(60vh, 480px)',
    maxWidth,
    className,
    style,
    viewportProps,
    children,
    ...rest
  },
  ref,
) {
  const fadeCls = fadeClass(fade, orientation);
  const outerStyle: CSSProperties = { ...style };
  const mh = toCssSize(maxHeight);
  const mw = toCssSize(maxWidth);
  /**
   * FF viewport is `height: 100%` (`size-full`). `max-height` alone does not
   * create a definite containing block, so content grows and never scrolls.
   * Mirror the cap onto `height` (skip `none`).
   */
  if (orientation !== 'horizontal' && mh && mh !== 'none') {
    if (outerStyle.maxHeight == null) outerStyle.maxHeight = mh;
    if (outerStyle.height == null) outerStyle.height = mh;
  }
  if (mw && outerStyle.maxWidth == null) {
    outerStyle.maxWidth = mw;
  }

  const { className: viewportPropClass, ...viewportRest } = viewportProps ?? {};

  return (
    <FluidScrollArea
      orientation={orientation}
      className={cx(styles.root, className)}
      style={outerStyle}
      data-wp-scroll-area-root=""
      data-wp-scroll-orientation={orientation}
      viewportClassName={cx(
        styles.viewport,
        orientation === 'vertical' && styles.vertical,
        orientation === 'horizontal' && styles.horizontal,
        orientation === 'both' && styles.both,
        fadeCls,
        viewportClassName,
        viewportPropClass,
      )}
      viewportRef={ref}
      viewportProps={{
        ...viewportRest,
        'data-wp-scroll-area': '',
        'data-wp-scroll-orientation': orientation,
      } as HTMLAttributes<HTMLDivElement>}
      {...rest}
    >
      {children}
    </FluidScrollArea>
  );
});
